import React, { useEffect, useRef, useMemo, useCallback, useReducer } from 'react';
import { Box } from '@mantine/core';
import DisconnectedScreen from './screens/0-DisconnectedScreen';
import JoinGameScreen from './screens/1-JoinGameScreen';
import PlayerLobbyScreen from './screens/2-PlayerLobbyScreen';
import PromptCollectionScreen from './screens/3-PromptCollectionScreen';
import PowerupSelectScreen from './screens/4-PowerupSelectScreen';
import PlayerDrawingScreen from './screens/5-PlayerDrawingScreen';
import JudgeVotingScreen from './screens/6-JudgeVotingScreen';
import WaitingScreen from './screens/7-WaitingScreen';
import ResultsPendingScreen from './screens/8-ResultsPendingScreen';
import GameFinishedScreen from './screens/10-GameFinishedScreen';
import useSocket from './hooks/useSocket';
import useGameActions from './hooks/useGameActions';
import useGameEvents from './hooks/useGameEvents';
import { gameReducer, initialGameState } from './state/gameReducer';
import { roundReducer, initialRoundState } from './state/roundReducer';

// Debug environment variables
console.log('Environment variables:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_SERVER_URL: process.env.REACT_APP_SERVER_URL
});

const waitingImages = {
  'waiting-for-prompt': '/doodle-submitted-bg.png',
  'waiting-for-responses': '/doodle-submitted-bg.png',
  'waiting-on-judge': '/waiting-on-judge-bg.png',
  'judge-voted': '/doodle-submitted-bg.png',
  'results-loser': '/loser-bg.png',
};

function App() {
  const [game, gameDispatch] = useReducer(gameReducer, initialGameState);
  const [round, roundDispatch] = useReducer(roundReducer, initialRoundState);

  // --- Hook wiring ---

  // actionsRef breaks the circular dependency: onRejoinSuccess needs syncStateFromServer,
  // but useSocket runs before useGameActions. The ref is populated after useGameActions returns,
  // and onRejoinSuccess only fires asynchronously on socket connect events.
  const actionsRef = useRef(null);

  const onRejoinSuccess = useCallback((rc, pn, response) => {
    gameDispatch({ type: 'SET_ROOM_CODE', roomCode: rc });
    gameDispatch({ type: 'SET_PLAYER_NAME', playerName: pn });
    actionsRef.current.syncStateFromServer(response);
    gameDispatch({ type: 'SET_STATUS', statusMessage: 'Reconnected!' });
  }, []);

  const onRejoinFailure = useCallback(() => {
    // saveSession/clearSession handled inside useSocket for auto-rejoin
  }, []);

  const { socket, connectionStatus, retryCount, persistentIdRef, saveSession, clearSession } =
    useSocket({ roomCode: game.roomCode, playerName: game.playerName, gameState: game.phase, onRejoinSuccess, onRejoinFailure });

  const actions = useGameActions({
    socket, connectionStatus, persistentIdRef, saveSession, clearSession,
    game, gameDispatch, round, roundDispatch,
  });
  actionsRef.current = actions;

  useGameEvents({ socket, game, gameDispatch, roundDispatch });

  // Auto-fill room code from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roomFromUrl = urlParams.get('room');
    if (roomFromUrl) {
      gameDispatch({ type: 'SET_ROOM_CODE', roomCode: roomFromUrl.toUpperCase() });
    }
  }, []);

  // Derive screen phase: flattens playing sub-states into top-level phases
  const showingResults = round.votingResults?.length > 0 && !round.votingPhase;
  const isWinner = showingResults && round.votingResults.find(r => r.rank === 1)?.playerId === game.playerId;

  const screenPhase = useMemo(() => {
    if (game.phase !== 'playing') return game.phase;
    if (round.promptCollectionPhase) return 'prompt-collection';
    if (round.powerupSelectPhase && !round.currentPrompt) return 'powerup-select';
    if (round.countdown > 0) return 'round-countdown';
    if (round.isWaitingForPrompt) return 'waiting-for-prompt';
    if (showingResults) return isWinner ? 'results-winner' : 'results-loser';
    if (round.votingPhase) {
      if (round.isJudge) return round.hasVoted ? 'judge-voted' : 'judge-voting';
      return 'waiting-on-judge';
    }
    if (round.currentPrompt) {
      if (round.isJudge) return round.hasDrawnAward ? 'waiting-for-responses' : 'drawing';
      return round.hasResponded ? 'waiting-for-responses' : 'drawing';
    }
    return 'waiting-for-prompt';
  }, [game.phase, round.promptCollectionPhase, round.powerupSelectPhase, round.countdown, round.isWaitingForPrompt,
      showingResults, isWinner, round.votingPhase, round.isJudge, round.hasVoted, round.currentPrompt,
      round.hasDrawnAward, round.hasResponded]);

  const headerProps = {
    gameName: 'Doodle Poodle',
    roomCode: game.roomCode,
    onReconnect: actions.reconnectToRoom,
    onQuit: actions.handleJoinNewGame,
  };

  const renderGameState = () => {
    switch (screenPhase) {
      case 'join':
        return (
          <JoinGameScreen
            gameName={headerProps.gameName}
            roomCode={game.roomCode}
            setRoomCode={(v) => gameDispatch({ type: 'SET_ROOM_CODE', roomCode: v })}
            playerName={game.playerName}
            setPlayerName={(v) => gameDispatch({ type: 'SET_PLAYER_NAME', playerName: v })}
            onJoinGame={actions.joinGame}
            statusMessage={game.statusMessage}
            connectionStatus={connectionStatus}
            retryCount={retryCount}
            socket={socket}
          />
        );

      case 'lobby':
        return (
          <PlayerLobbyScreen
            gameName={headerProps.gameName}
            roomCode={game.roomCode}
            playerName={game.playerName}
            playerScore={game.playerScore}
            players={game.players}
            isGameController={game.isGameController}
            onStartGame={actions.startGame}
            gameOptions={game.gameOptions}
            onGameOptionChange={actions.handleGameOptionChange}
            onQuit={actions.handleJoinNewGame}
          />
        );

      case 'prompt-collection':
        return (
          <PromptCollectionScreen
            {...headerProps}
            promptsPerPlayer={round.promptsPerPlayer}
            submittedPrompts={round.submittedPrompts}
            promptText={round.promptText}
            setPromptText={(v) => roundDispatch({ type: 'SET_PROMPT_TEXT', promptText: v })}
            onSubmitPrompt={() => actions.submitPrompt(round.promptText)}
            promptTimeLeft={round.promptTimeLeft}
            disableTimers={game.gameOptions.disableTimers}
          />
        );

      case 'powerup-select':
        return (
          <PowerupSelectScreen
            {...headerProps}
            myPowerups={round.myPowerups}
            players={game.players}
            playerId={game.playerId}
            currentJudgeId={round.currentJudgeId}
            currentJudgeName={round.currentJudgeName}
            currentRound={round.currentRound}
            totalRounds={round.totalRounds}
            judgeAnnouncementDuration={round.judgeAnnouncementDuration}
            powerupUsedThisRound={round.powerupUsedThisRound}
            onUsePowerup={actions.usePowerup}
          />
        );

      case 'drawing':
        return (
          <PlayerDrawingScreen
            {...headerProps}
            onSubmitDrawing={round.isJudge ? actions.submitJudgeAward : actions.submitDrawing}
            disabled={!round.isJudge && round.hasResponded}
            instruction={round.isJudge
              ? { text: `JUDGE — Draw an award for: "${round.currentPrompt}"`, color: 'yellow' }
              : { text: `Draw: "${round.currentPrompt}"`, color: 'purple' }}
            simplifiedDrawing={game.gameOptions.simplifiedDrawing}
            responseTimeLeft={round.responseTimeLeft}
            disableTimers={game.gameOptions.disableTimers}
            isJudge={round.isJudge}
            activePowerupEffects={round.activePowerupEffects}
          />
        );

      case 'judge-voting':
        return (
          <JudgeVotingScreen
            {...headerProps}
            votingResponses={round.votingResponses}
            playerId={game.playerId}
            onSubmitVote={actions.submitVote}
            votingTimeLeft={round.votingTimeLeft}
            disableTimers={game.gameOptions.disableTimers}
          />
        );

      case 'results-winner':
      case 'results-loser':
      case 'round-countdown':
        return (
          <WaitingScreen
            {...headerProps}
            image="/round-countdown-bg.png"
            timerSeconds={round.countdown}
          />
        );

      case 'waiting-for-prompt':
      case 'waiting-for-responses':
        return <WaitingScreen {...headerProps} image={waitingImages[screenPhase]} />;

      case 'waiting-on-judge':
      case 'judge-voted':
        return (
          <ResultsPendingScreen
            {...headerProps}
            timerSeconds={!game.gameOptions.disableTimers ? round.votingTimeLeft : 0}
          />
        );

      case 'finished':
        return (
          <GameFinishedScreen
            gameName={headerProps.gameName}
            roomCode={game.roomCode}
            playerName={game.playerName}
            playerScore={game.playerScore}
            finalResults={game.finalResults}
            playerId={game.playerId}
            onJoinNewGame={actions.handleJoinNewGame}
            isGameController={game.isGameController}
            onStartNewGame={actions.handleStartNewGame}
          />
        );

      case 'disconnected':
        return (
          <DisconnectedScreen
            statusMessage={game.statusMessage}
            onJoinNewGame={actions.handleJoinNewGame}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {renderGameState()}
    </Box>
  );
}

export default App;
